const RESUME_SYNC_QUEUE_KEY = 'rastyle.activity.resume-sync.v1';
const MAX_RESUME_SYNC_QUEUE_SIZE = 80;
const BASE_RETRY_MS = 5_000;
const MAX_RETRY_MS = 5 * 60_000;

const safeStorage = () => {
  if (typeof window === 'undefined') return null;
  try { return window.localStorage; } catch { return null; }
};

const readResumeSyncQueue = () => {
  const storage = safeStorage();
  if (!storage) return [];
  try {
    const value = JSON.parse(storage.getItem(RESUME_SYNC_QUEUE_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const writeResumeSyncQueue = (items) => {
  const storage = safeStorage();
  if (!storage) return items;
  try { storage.setItem(RESUME_SYNC_QUEUE_KEY, JSON.stringify(items)); } catch { /* noop */ }
  return items;
};

const resumeSyncKey = (item = {}) => `${item.entity_type || 'content'}:${item.entity_id || ''}`;

const retryDelay = (attempts = 0) => Math.min(MAX_RETRY_MS, BASE_RETRY_MS * (2 ** Math.max(0, attempts)));

const enqueueResumeSync = (payload = {}) => {
  if (!payload.entity_id) return readResumeSyncQueue();
  const now = new Date().toISOString();
  const item = {
    ...payload,
    attempts: 0,
    queued_at: now,
    next_attempt_at: now,
  };
  const key = resumeSyncKey(item);
  const next = [
    ...readResumeSyncQueue().filter((entry) => resumeSyncKey(entry) !== key),
    item,
  ].slice(-MAX_RESUME_SYNC_QUEUE_SIZE);
  writeResumeSyncQueue(next);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rastyle-resume-sync-pending'));
  }
  return next;
};

const dueResumeSyncItems = (items, now = Date.now()) => items.filter((item) => {
  const due = Date.parse(item.next_attempt_at || item.queued_at || '');
  return !Number.isFinite(due) || due <= now;
});

const flushResumeSyncQueue = async ({ apiBase, token, fetchImpl = globalThis.fetch, now = Date.now() } = {}) => {
  const queue = readResumeSyncQueue();
  if (!queue.length || !apiBase || typeof fetchImpl !== 'function') {
    return { sent: 0, pending: queue.length, failed: 0 };
  }

  const due = dueResumeSyncItems(queue, now);
  if (!due.length) return { sent: 0, pending: queue.length, failed: 0 };

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const anonymousId = queue[0]?.anonymous_id;
  const kept = queue.filter((item) => !due.includes(item));
  let sent = 0;
  let failed = 0;

  for (const item of due) {
    try {
      const response = await fetchImpl(`${apiBase}/api/activity/resume?anonymous_id=${encodeURIComponent(item.anonymous_id || anonymousId || 'anonymous')}`, {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          entity_id: item.entity_id,
          entity_type: item.entity_type || 'content',
          position_seconds: item.position_seconds || 0,
          duration_seconds: item.duration_seconds || 0,
          completed: Boolean(item.completed),
          metadata: item.metadata || {},
        }),
      });
      if (!response.ok) throw new Error(`Resume sync failed: ${response.status}`);
      sent += 1;
    } catch {
      failed += 1;
      const attempts = Number(item.attempts || 0) + 1;
      kept.push({
        ...item,
        attempts,
        next_attempt_at: new Date(now + retryDelay(attempts - 1)).toISOString(),
      });
    }
  }

  writeResumeSyncQueue(kept.slice(-MAX_RESUME_SYNC_QUEUE_SIZE));
  return { sent, pending: kept.length, failed };
};

export {
  BASE_RETRY_MS,
  MAX_RETRY_MS,
  RESUME_SYNC_QUEUE_KEY,
  dueResumeSyncItems,
  enqueueResumeSync,
  flushResumeSyncQueue,
  readResumeSyncQueue,
  resumeSyncKey,
  retryDelay,
};
